import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const HeroSection = () => {
  return (
    <section className="relative bg-gradient-to-br from-background via-background to-primary/5 py-20 lg:py-32 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent rounded-full blur-3xl"></div>
      </div>
      
      <div className="container mx-auto px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <div className="text-center lg:text-left">
            {/* Large Logo Display */}
            <div className="flex justify-center lg:justify-start mb-8">
              <Image
                src="/assets/images/image-1760433114854.png"
                alt="Workfolio logo featuring two circular figures representing people within a circular border, next to bold WORKFOLIO text in dark blue - Norway's first anonymous recruitment platform"
                className="h-16 lg:h-20 w-auto"
              />
            </div>
            
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
              <Icon name="Shield" size={16} className="mr-2" />
              Anonymous • Fair • Efficient
            </div>
            
            <h1 className="text-4xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
              Hire Based on
              <span className="text-primary block">Skills, Not Bias</span>
            </h1>
            
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl">
              Norway's first anonymous recruitment platform. Job seekers create profiles for free, 
              employers discover talent without bias and pay only when they find the right match.
            </p>
            
            {/* Problem-Solution */}
            <div className="bg-card/50 backdrop-blur-sm rounded-2xl p-6 mb-8 border border-border/50">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="text-left">
                  <div className="flex items-center mb-3">
                    <Icon name="AlertCircle" size={20} className="text-destructive mr-2" />
                    <h3 className="font-semibold text-foreground">The Problem</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Traditional hiring is biased by names, photos, and demographics, 
                    leading to unfair rejections and missed talent.
                  </p>
                </div>
                <div className="text-left">
                  <div className="flex items-center mb-3">
                    <Icon name="CheckCircle" size={20} className="text-success mr-2" />
                    <h3 className="font-semibold text-foreground">Our Solution</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Anonymous profiles focus on skills and experience first, 
                    revealing identity only after genuine interest.
                  </p>
                </div>
              </div>
            </div>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button size="lg" asChild className="text-lg px-8 py-4">
                <Link to="/role-selection-modal">
                  <Icon name="UserPlus" size={20} className="mr-2" />
                  Get Started Free
                </Link>
              </Button>
              
              <Button variant="outline" size="lg" asChild className="text-lg px-8 py-4">
                <Link to="#how-it-works">
                  <Icon name="Play" size={20} className="mr-2" />
                  See How It Works
                </Link>
              </Button>
            </div>
            
            {/* Trust Indicators */}
            <div className="flex items-center justify-center lg:justify-start gap-6 mt-8 text-sm text-muted-foreground">
              <div className="flex items-center">
                <Icon name="Shield" size={16} className="mr-1 text-success" />
                GDPR Compliant
              </div>
              <div className="flex items-center">
                <Icon name="MapPin" size={16} className="mr-1 text-primary" />
                Made in Norway
              </div>
              <div className="flex items-center">
                <Icon name="Users" size={16} className="mr-1 text-accent" />
                500+ Companies
              </div>
            </div>
          </div>
          
          {/* Hero Image */}
          <div className="relative">
            <div className="relative z-10">
              <Image
                src="https://images.unsplash.com/photo-1538688423619-a81d3f23454b"
                alt="Diverse group of professionals collaborating in modern office environment with laptops and documents"
                className="rounded-2xl shadow-elevation-lg w-full h-[500px] object-cover" />

              
              {/* Floating Cards */}
              <div className="absolute -top-4 -left-4 bg-card rounded-xl p-4 shadow-elevation border border-border animate-float">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon name="User" size={20} className="text-primary" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground">Anonymous #1247</div>
                    <div className="text-xs text-muted-foreground">React Developer</div>
                  </div>
                </div>
              </div>
              
              <div className="absolute -bottom-4 -right-4 bg-card rounded-xl p-4 shadow-elevation border border-border animate-float" style={{ animationDelay: '1s' }}>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-success/10 rounded-full flex items-center justify-center">
                    <Icon name="CheckCircle" size={20} className="text-success" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground">Profile Unlocked</div>
                    <div className="text-xs text-muted-foreground">149 NOK</div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Background Decoration */}
            <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 to-accent/20 rounded-2xl blur-3xl -z-10 transform scale-110"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;